import { Link } from "react-router-dom";
import { Instagram, MapPin, Phone, Mail } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "@/lib/business-info";

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-charcoal-900 text-cream/70"
        >
            {/* hairline */}
            <div className="empire-container border-t border-cream/10" />

            {/* Brand mark */}
            <div className="empire-container pt-20 md:pt-28 pb-12 md:pb-16">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                    <div>
                        <img
                            src="/brand/empire-plaque-360.png"
                            srcSet="/brand/empire-plaque-240.png 1x, /brand/empire-plaque-480.png 2x"
                            alt="Empire Jamaica Fusion seal"
                            width="172"
                            height="96"
                            loading="lazy"
                            decoding="async"
                            className="h-20 md:h-24 w-auto"
                            data-testid="footer-logo"
                        />
                        <p className="empire-eyebrow mt-6">
                            Jamaica Fusion · Poughkeepsie NY
                        </p>
                    </div>
                    <p
                        className="font-serif italic text-cream/70 text-[1.25rem] md:text-[1.5rem] max-w-md leading-snug tracking-tight"
                        data-testid="footer-brand"
                    >
                        Caribbean cooking, plated with patience.<br className="hidden md:block" />
                        Served on Main Street since day one.
                    </p>
                </div>
            </div>

            {/* Columns */}
            <div className="empire-container grid grid-cols-1 md:grid-cols-12 gap-12 pb-16">
                {/* Visit */}
                <div className="md:col-span-4">
                    <h3 className="empire-label mb-5">Visit</h3>
                    <address className="not-italic space-y-3 font-light">
                        <div className="flex items-start gap-3">
                            <MapPin size={16} className="mt-1 text-gold shrink-0" />
                            <div>
                                <div className="text-cream">{BUSINESS.address.line1}</div>
                                <div>{BUSINESS.address.line2}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Phone size={16} className="text-gold shrink-0" />
                            <a
                                href={BUSINESS.phoneLink}
                                data-testid="footer-phone"
                                className="empire-link"
                            >
                                {BUSINESS.phoneDisplay}
                            </a>
                        </div>
                        <div className="flex items-center gap-3">
                            <Mail size={16} className="text-gold shrink-0" />
                            <a
                                href={BUSINESS.emailLink}
                                data-testid="footer-email"
                                className="empire-link"
                            >
                                {BUSINESS.email}
                            </a>
                        </div>
                    </address>
                </div>

                {/* Hours */}
                <div className="md:col-span-3">
                    <h3 className="empire-label mb-5">Hours</h3>
                    <ul className="space-y-2 font-light text-[14px]">
                        {BUSINESS.hours.map((h) => (
                            <li
                                key={h.day}
                                className="flex items-baseline justify-between gap-4"
                            >
                                <span className="text-cream/85">{h.day}</span>
                                <span className="flex-1 mx-3 border-b border-cream/10 translate-y-[-3px]" />
                                <span
                                    className={
                                        h.hours === "Closed"
                                            ? "text-burgundy uppercase text-[11px] tracking-[0.18em]"
                                            : "text-cream/65"
                                    }
                                >
                                    {h.hours}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Explore */}
                <div className="md:col-span-3">
                    <h3 className="empire-label mb-5">Explore</h3>
                    <ul className="space-y-3 font-light text-[14px]">
                        {NAV_LINKS.map((l) => (
                            <li key={l.path}>
                                <Link
                                    to={l.path}
                                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="empire-link"
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Ordering & Social */}
                <div className="md:col-span-2">
                    <h3 className="empire-label mb-5">Ordering</h3>
                    <div data-testid="footer-ordering" className="space-y-2">
                        <p className="font-light text-[14px] text-cream/85">
                            Coming Soon
                        </p>
                        <p className="font-light text-[12.5px] text-cream/45 leading-relaxed">
                            Online ordering will be available soon.
                        </p>
                    </div>

                    <h3 className="empire-label mt-8 mb-5">Follow</h3>
                    <div className="flex items-center gap-3">
                        <a
                            href={BUSINESS.social.instagram.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-social-instagram"
                            aria-label="Instagram"
                            className="inline-flex h-10 w-10 items-center justify-center border border-cream/15 text-cream/70 hover:text-gold hover:border-gold transition-colors"
                        >
                            <Instagram size={16} />
                        </a>
                    </div>
                </div>
            </div>

            {/* Base */}
            <div className="border-t border-cream/10">
                <div className="empire-container py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[12px] text-cream/45">
                    <p>
                        © {new Date().getFullYear()} {BUSINESS.name}. All rights
                        reserved.
                    </p>
                    <p className="font-light">
                        Crafted with soul in the Hudson Valley.
                    </p>
                </div>
            </div>
        </footer>
    );
}
